import {useState, useCallback, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {debounce} from 'lodash';

export const useDocumentFilters = (initialFilters = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [filters, setFilters] = useState(initialFilters);
  const [searchValue, setSearchValue] = useState(initialFilters.freeSearch);

  const getFiltersFromQuery = useCallback(
    queryParams => ({
      countryCode: queryParams.countryCode
        ? queryParams.countryCode.split(',')
        : [],
      documentType: queryParams.documentType
        ? queryParams.documentType.split(',')
        : [],
      state: queryParams.state ? queryParams.state.split(',') : [],
      documentVersion: queryParams.documentVersion
        ? queryParams.documentVersion.split(',')
        : [],
      yearOfIssue: queryParams.yearOfIssue
        ? queryParams.yearOfIssue.split(',')
        : [],
      alias: queryParams.alias ? queryParams.alias.split(',') : [],
      statusCode: queryParams.statusCode
        ? queryParams.statusCode.split(',')
        : [],
      supportedFrom: queryParams.supportedFrom
        ? queryParams.supportedFrom.split(',')
        : [],
      freeSearch: queryParams.freeSearch
        ? queryParams.freeSearch.split(',')
        : [],
    }),
    []
  );

  const buildQueryString = useCallback(
    (updatedFilters, pageNum, pageSize) =>
      queryString.stringify(
        {
          ...updatedFilters,
          page: pageNum,
          pageSize: pageSize,
        },
        {arrayFormat: 'comma'}
      ),
    []
  );

  const debouncedFilterChange = useMemo(
    () =>
      debounce((key, value, callback) => {
        const updatedFilters = {...filters, [key]: value};
        setFilters(updatedFilters);
        callback?.(updatedFilters);
      }, 300),
    [filters]
  );

  const updateFilters = useCallback(
    (key, value, callback) => {
      debouncedFilterChange(key, value, callback);
    },
    [debouncedFilterChange]
  );

  const clearFilters = useCallback(
    (pageSize, callback) => {
      setFilters({});
      setSearchValue(null);

      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', pageSize.toString());

      navigate(`${location?.pathname}?${params.toString()}`, {replace: true});
      callback?.({});
    },
    [navigate, location]
  );

  const updateSearchValue = useCallback(value => {
    setSearchValue(value);
  }, []);

  return {
    filters,
    searchValue,
    setFilters,
    updateFilters,
    clearFilters,
    updateSearchValue,
    getFiltersFromQuery,
    buildQueryString,
  };
};
